// Ranked distinctive terms for the X-ray lens — tokenize prompt summaries,
// count unigram + adjacent-bigram document frequency, and rank by contrast
// (lift) against a baseline corpus — the org-wide window — so a scoped
// X-ray leads with what is DIFFERENT about its slice instead of the
// tenant-wide vocabulary. Without a baseline (the org scope itself) the
// ranking falls back to plain document frequency. Pure functions, no DOM,
// no randomness — the same window always produces the same list, and the
// whole pipeline is unit-testable.

// Generic English plus the trace-summary scaffolding that opens nearly
// every summary ("User reports that…", "User points out…") — universal
// across scopes, so it carries no contrast. Kept deliberately modest:
// over-stopping starts deleting signal (e.g. "finding", "error").
const STOPWORDS = new Set([
    // articles / conjunctions / prepositions / pronouns / auxiliaries
    "the", "and", "for", "are", "but", "not", "you", "your", "with", "this",
    "that", "these", "those", "from", "into", "over", "after", "before",
    "while", "within", "without", "between", "during", "against", "about",
    "above", "below", "under", "their", "them", "then", "there", "they",
    "what", "when", "where", "which", "who", "whose", "how", "why", "will",
    "would", "could", "should", "can", "may", "might", "must", "shall",
    "has", "have", "had", "was", "were", "been", "being", "does", "did",
    "doing", "all", "any", "both", "each", "few", "more", "most", "other",
    "some", "such", "only", "own", "same", "than", "too", "very", "just",
    "because", "until", "again", "further", "once", "here", "also", "its",
    "it's", "out", "off", "now", "one", "two", "via", "per", "due", "etc",
    // trace-summary scaffolding
    "user", "users", "assistant", "prompt", "prompts", "reports", "report",
    "reported", "points", "submitted", "submits", "asks", "asked",
    "requests", "requested", "says", "said", "wants", "needs", "notes",
    "noted", "provides", "provided",
]);

// Tokens start with a letter and run ≥3 chars; hyphens/apostrophes/
// underscores stay inside so "self-review", "o-1996", "doesn't" survive
// as single terms. Paths split on "/" for free (it's not in the class).
// Stopwords stay IN the stream so they break bigram adjacency ("review
// that code" yields no pair) — but sub-3-char function words (of/to/in)
// never enter the stream, so pairs bridge them: "review of code" reads
// as "review code", which is the phrase a human would name anyway.
function tokenStream(summary) {
    return String(summary || "").toLowerCase().match(/[a-z][a-z0-9'_-]{2,}/g) || [];
}

/**
 * The countable terms of one summary: non-stopword unigrams plus adjacent
 * non-stopword bigrams ("code review"). Returns a SET — one vote per
 * document, so a term repeated within one summary still counts once.
 */
export function termsOf(summary) {
    const out = new Set();
    const stream = tokenStream(summary);
    for (let i = 0; i < stream.length; i++) {
        const t = stream[i];
        if (STOPWORDS.has(t)) continue;
        out.add(t);
        const next = stream[i + 1];
        if (next && next !== t && !STOPWORDS.has(next)) out.add(`${t} ${next}`);
    }
    return out;
}

/**
 * Does this summary carry the term? Uses the SAME extraction as the
 * ranking, so a listed term always matches at least the docs that voted
 * for it — a substring test would miss bigrams split by punctuation.
 */
export function summaryHasTerm(summary, term) {
    return termsOf(summary).has(String(term || "").toLowerCase());
}

function docsOf(summaries) {
    const docs = [];
    for (const s of summaries || []) {
        const t = termsOf(s);
        if (t.size) docs.push(t);
    }
    return docs;
}

function countTerms(docs) {
    const counts = new Map();
    for (const doc of docs) {
        for (const t of doc) counts.set(t, (counts.get(t) || 0) + 1);
    }
    return counts;
}

/**
 * Rank a window's terms, optionally by contrast against a baseline corpus.
 *
 * Each kept term carries its document count, its share of the window's
 * docs, and — when a non-empty baseline is given — its smoothed lift
 * (how many times more common per-doc it is here than in the baseline).
 * Score is `count · log2(lift)` with a baseline (volume × contrast, so a
 * generic term at lift≈1 sinks regardless of how common it is), plain
 * `count` without one. The document-frequency floor mirrors the small-
 * window relaxation the old term graph used: singleton terms only count
 * once the window is big enough for recurrence to mean something.
 *
 * @returns {{ terms: {term, kind, count, share, lift}[], docCount: number, distinct: number }}
 */
export function buildTermRank(summaries, baselineSummaries = null, { maxTerms = 40 } = {}) {
    const docs = docsOf(summaries);
    const counts = countTerms(docs);
    const baseDocs = docsOf(baselineSummaries);
    const baseCounts = baseDocs.length ? countTerms(baseDocs) : null;

    const nS = docs.length;
    const nB = baseDocs.length;
    const floor = nS >= 40 ? 2 : 1;
    const candidates = [];
    for (const [term, count] of counts) {
        if (count < floor) continue;
        let lift = null;
        if (baseCounts) {
            const cb = baseCounts.get(term) || 0;
            lift = ((count + 0.5) / (nS + 1)) / ((cb + 0.5) / (nB + 1));
        }
        candidates.push({
            term,
            kind: term.includes(" ") ? "bigram" : "unigram",
            count,
            share: nS ? count / nS : 0,
            lift,
            score: lift != null ? count * Math.log2(lift) : count,
        });
    }
    candidates.sort((x, y) => (y.score - x.score) || (y.count - x.count) || x.term.localeCompare(y.term));
    return {
        terms: candidates.slice(0, maxTerms).map(({ score: _score, ...t }) => t),
        docCount: nS,
        distinct: candidates.length,
    };
}
